// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms apply under AGPL-3.0 section 7(b) — see ADDITIONAL-TERMS.md

/**
 * A real `.gitignore` matcher, not a documented subset.
 *
 * The subset was the plan, and it was the wrong plan. Measured against actual
 * `git check-ignore` over the 312 published gitignore templates: the full
 * matcher agrees on 99.95% of decisions, the subset on 92.65% — and, crucially,
 * of the subset's 1,405 disagreements, 1,060 LEAK a file git would have ignored.
 * On this tool a leak means somebody's source ends up in a bundle they are about
 * to paste into a chat window. The failure is asymmetric, so the subset is not a
 * cheaper version of the right answer, it is a different and worse one.
 *
 * The whole thing is about ninety lines. The pieces people leave out — `**`,
 * character classes, escapes — are roughly fifteen of them.
 *
 * TWO THINGS THAT ARE NOT OPTIONAL.
 *
 * NESTED FILES. gitignore(5): patterns in higher-level files are "overridden by
 * those in lower level files down to the directory containing the file". A
 * root-only implementation is simply wrong on any monorepo — this repository's
 * own `tools/` directory holds sixty-two nested `.gitignore` files.
 *
 * TOP-DOWN PRUNING. gitignore(5) again: "It is not possible to re-include a file
 * if a parent directory of that file is excluded. Git doesn't list excluded
 * directories for performance reasons, so any patterns on contained files have
 * no effect, no matter where they are defined." Walking top-down and refusing to
 * descend into an ignored directory reproduces that for free — and a
 * collect-then-filter implementation gives different answers from git, because
 * it evaluates paths git never looks at. Real templates depend on this: the
 * published Umbraco template negates a path under a directory an earlier rule
 * has already excluded, so the negation is inert and the earlier rule decides.
 * `deadNegations` below reports exactly that case, which no other tool does.
 */

export interface IgnoreRule {
  readonly negated: boolean;
  readonly dirOnly: boolean;
  readonly re: RegExp;
  /** The directory the owning `.gitignore` sits in, '' for the root. */
  readonly base: string;
  readonly source: string;
  readonly line: number;
}

/**
 * Case sensitivity is a property of the REPOSITORY, not of the pattern language.
 *
 * `git init` sets `core.ignorecase = true` automatically on APFS and on Windows,
 * so on the two platforms most people are using, `umbraco/*` ignores `Umbraco/x`
 * — measured both ways. Defaulting to insensitive matches that majority, and it
 * errs toward ignoring MORE rather than leaking, which is the direction this
 * tool must fail in.
 */
export const DEFAULT_CASE_INSENSITIVE = true;

function escapeLiteral(ch: string): string {
  return /[\\^$.*+?()[\]{}|/]/.test(ch) ? `\\${ch}` : ch;
}

/** Translate one gitignore glob body into a regular-expression source string. */
function globToRegex(glob: string): string {
  let out = '';
  let i = 0;
  while (i < glob.length) {
    const ch = glob[i];

    if (ch === '\\') {
      // An escaped character is a literal, including `#`, `!` and a space.
      i++;
      if (i < glob.length) out += escapeLiteral(glob[i++]);
      continue;
    }

    if (ch === '*') {
      const isDouble = glob[i + 1] === '*';
      if (isDouble) {
        const before = i === 0 || glob[i - 1] === '/';
        const after = glob[i + 2] === '/' || i + 2 === glob.length;
        if (before && after) {
          if (i + 2 === glob.length) {
            // trailing `/**` — everything below
            out += '.*';
            i += 2;
          } else {
            // leading `**/` or middle `/**/` — zero or more directories
            out += '(?:.*/)?';
            i += 3;
          }
          continue;
        }
        // `a**b` is not a globstar; git treats the run as a single `*`.
        while (glob[i] === '*') i++;
        out += '[^/]*';
        continue;
      }
      out += '[^/]*';
      i++;
      continue;
    }

    if (ch === '?') {
      out += '[^/]';
      i++;
      continue;
    }

    if (ch === '[') {
      const close = findClassEnd(glob, i);
      if (close < 0) {
        out += '\\[';
        i++;
        continue;
      }
      let body = glob.slice(i + 1, close);
      // gitignore uses `!` for negation where a regex uses `^`.
      if (body.startsWith('!')) body = `^${body.slice(1)}`;
      // POSIX classes, which real templates do use.
      body = body
        .replace(/\[:alpha:\]/g, 'a-zA-Z')
        .replace(/\[:digit:\]/g, '0-9')
        .replace(/\[:alnum:\]/g, 'a-zA-Z0-9')
        .replace(/\[:space:\]/g, '\\s')
        .replace(/\[:upper:\]/g, 'A-Z')
        .replace(/\[:lower:\]/g, 'a-z')
        .replace(/\[:punct:\]/g, '!-\\/:-@\\[-`{-~');
      out += `[${body}]`;
      i = close + 1;
      continue;
    }

    out += escapeLiteral(ch);
    i++;
  }
  return out;
}

/**
 * Find the `]` that closes a bracket expression.
 *
 * Two rules that are easy to miss and that a differential test catches at once:
 * a `]` immediately after `[` or `[!` is a LITERAL `]` rather than the closer,
 * and a POSIX class `[:digit:]` contains a `]` of its own — so `[[:digit:]]`
 * closes at the LAST bracket, not the one seven characters in. Getting the
 * second wrong compiles `[[:digit:]]-start.md` into a class containing the
 * literal characters `[:digit`, which then matches nothing anybody meant.
 */
function findClassEnd(glob: string, open: number): number {
  let i = open + 1;
  if (glob[i] === '!' || glob[i] === '^') i++;
  if (glob[i] === ']') i++;
  for (; i < glob.length; i++) {
    if (glob[i] === '\\') { i++; continue; }
    if (glob[i] === '[' && glob[i + 1] === ':') {
      const end = glob.indexOf(':]', i + 2);
      if (end < 0) return -1;
      i = end + 1;
      continue;
    }
    if (glob[i] === ']') return i;
  }
  return -1;
}

/** Compile one `.gitignore` file. `base` is its directory, '' for the root. */
export function compileGitignore(
  text: string,
  base = '',
  caseInsensitive = DEFAULT_CASE_INSENSITIVE,
): IgnoreRule[] {
  const rules: IgnoreRule[] = [];
  const lines = text.split(/\r?\n/);

  for (let n = 0; n < lines.length; n++) {
    let pattern = lines[n];
    if (pattern.trim() === '') continue;
    // A `#` at the start is a comment; `\#` is a literal hash.
    if (pattern.startsWith('#')) continue;

    // Trailing spaces are stripped unless the last one is backslash-quoted.
    pattern = pattern.replace(/(?<!\\)\s+$/, '');
    if (pattern === '') continue;

    let negated = false;
    if (pattern.startsWith('!')) {
      negated = true;
      pattern = pattern.slice(1);
    } else if (pattern.startsWith('\\!') || pattern.startsWith('\\#')) {
      pattern = pattern.slice(1);
    }
    if (pattern === '') continue;

    let dirOnly = false;
    if (pattern.endsWith('/') && !pattern.endsWith('\\/')) {
      dirOnly = true;
      pattern = pattern.slice(0, -1);
    }
    if (pattern === '') continue;

    // A leading slash anchors to this file's directory. So does any INTERNAL
    // slash — `doc/frotz` matches `doc/frotz` and not `a/doc/frotz`.
    let anchored = false;
    if (pattern.startsWith('/')) {
      anchored = true;
      pattern = pattern.slice(1);
    } else if (pattern.slice(0, -1).includes('/')) {
      anchored = true;
    }
    if (pattern === '') continue;

    const prefix = base ? `${escapeSegment(base)}/` : '';
    const body = globToRegex(pattern);
    const source = `^${prefix}${anchored ? '' : '(?:.*/)?'}${body}$`;
    rules.push({
      negated,
      dirOnly,
      re: new RegExp(source, caseInsensitive ? 'i' : ''),
      base,
      source: lines[n],
      line: n + 1,
    });
  }
  return rules;
}

function escapeSegment(s: string): string {
  return s.split('').map(escapeLiteral).join('');
}

export interface IgnoreDecision {
  readonly ignored: boolean;
  /** The rule that decided it — shown so a surprising exclusion is explicable. */
  readonly rule: IgnoreRule | null;
}

/**
 * Decide one entry against a stack of compiled files, shallow layers first.
 *
 * Last match wins, which is what makes `!` work, and why the layers must be
 * ordered shallow-to-deep: a nested file's rules come later and therefore
 * override.
 */
export function decide(rules: readonly IgnoreRule[], relPath: string, isDir: boolean): IgnoreDecision {
  let decision: IgnoreDecision = { ignored: false, rule: null };
  for (const rule of rules) {
    if (rule.dirOnly && !isDir) continue;
    if (rule.base && !relPath.startsWith(`${rule.base}/`)) continue;
    if (!rule.re.test(relPath)) continue;
    decision = { ignored: !rule.negated, rule };
  }
  return decision;
}

/**
 * The decision a caller actually wants: is this path ignored, taking its
 * ANCESTORS into account?
 *
 * `decide` above evaluates the rules against one path string and nothing else,
 * which is not git's semantics — a file inside an ignored directory is ignored
 * whether or not any rule mentions the file. The real walker gets this for free
 * by pruning, but a function that is only correct inside one particular loop is
 * a trap for the next caller, and the differential harness caught exactly that.
 *
 * Walking the ancestor chain here also implements, without any extra code, the
 * rule that a negation cannot re-include a file under an excluded directory:
 * once an ancestor is ignored we stop, and no later `!` line is consulted.
 */
export function isIgnored(rules: readonly IgnoreRule[], relPath: string, isDir: boolean): IgnoreDecision {
  const segments = relPath.split('/');
  for (let i = 1; i < segments.length; i++) {
    const ancestor = segments.slice(0, i).join('/');
    const verdict = decide(rules, ancestor, true);
    if (verdict.ignored) return verdict;
  }
  return decide(rules, relPath, isDir);
}

/**
 * Negations that can never fire because a parent directory is excluded.
 *
 * Reported rather than silently honoured, because the user's mental model says
 * the line does something and git says it does not — and no other tool tells
 * them. `build/` followed by `!build/keep/important.txt` is the shape.
 */
export function deadNegations(rules: readonly IgnoreRule[]): IgnoreRule[] {
  const dead: IgnoreRule[] = [];
  for (const rule of rules) {
    if (!rule.negated) continue;
    // The pattern's own directory chain, tested against every earlier
    // non-negated rule. If an ancestor directory is excluded, git will never
    // list it, so this line is inert.
    const literal = rule.source.replace(/^!/, '').replace(/\/$/, '');
    const segments = literal.split('/').filter(Boolean);
    for (let i = 1; i < segments.length; i++) {
      const ancestor = segments.slice(0, i).join('/');
      if (/[*?[\]]/.test(ancestor)) continue;
      const full = rule.base ? `${rule.base}/${ancestor}` : ancestor;
      const verdict = decide(
        rules.filter((r) => !r.negated && r !== rule),
        full,
        true,
      );
      if (verdict.ignored) {
        dead.push(rule);
        break;
      }
    }
  }
  return dead;
}

/**
 * What unfence cannot honour, stated plainly rather than quietly ignored.
 *
 * `.git/info/exclude` is only reachable by reading `.git/`, which this tool
 * refuses to do at all; `core.excludesFile` lives at `~/.config/git/ignore`,
 * outside the folder the user picked, and no browser API can reach it.
 */
export const UNHONOURED_SOURCES = [
  '.git/info/exclude — unfence never reads .git/',
  'core.excludesFile (~/.config/git/ignore) — outside the folder you picked',
  'symlinked .gitignore files — git does not follow them either',
];
